"use client"

import React from "react"
import { Clock } from "@/components/clock"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"

function formatTimeWithSeconds(dateToFormat: Date, tz: "local" | "utc", timeFormat: "12h" | "24h"): string {
  let hours24: number
  let minutes: number
  let seconds: number

  if (tz === "utc") {
    hours24 = dateToFormat.getUTCHours()
    minutes = dateToFormat.getUTCMinutes()
    seconds = dateToFormat.getUTCSeconds()
  } else {
    hours24 = dateToFormat.getHours()
    minutes = dateToFormat.getMinutes()
    seconds = dateToFormat.getSeconds()
  }

  const formatSegment = (segment: number): string => {
    return segment < 10 ? `0${segment}` : segment.toString()
  }

  const getHours = (hours24: number, format: "12h" | "24h"): number => {
    if (format === "24h") return hours24
    return hours24 % 12 === 0 ? 12 : hours24 % 12
  }

  const getAmPm = (hours24: number): string => (hours24 >= 12 ? "PM" : "AM")

  const hours = getHours(hours24, timeFormat)
  const timeStr = `${formatSegment(hours)}:${formatSegment(minutes)}:${formatSegment(seconds)}`
  return timeFormat === "12h" ? `${timeStr} ${getAmPm(hours24)}` : timeStr
}

function formatFullDateTime(dateToFormat: Date, tz: "local" | "utc", timeFormat: "12h" | "24h"): string {
  const dateStr =
    tz === "utc"
      ? dateToFormat.toLocaleDateString("en-NZ", {
          year: "numeric",
          month: "long",
          day: "numeric",
          timeZone: "UTC",
        })
      : dateToFormat.toLocaleDateString("en-NZ", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
  const timeStr = formatTimeWithSeconds(dateToFormat, tz, timeFormat)
  return `${dateStr} ${timeStr}`
}

function getTimezoneAbbreviation(dateToFormat: Date): string {
  try {
    const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone
    const shortFormatter = new Intl.DateTimeFormat("en", { timeZoneName: "short" })
    const shortParts = shortFormatter.formatToParts(dateToFormat)
    const shortTZ = shortParts.find((part) => part.type === "timeZoneName")?.value ?? ""
    const offset = -dateToFormat.getTimezoneOffset()
    const hours = Math.floor(Math.abs(offset) / 60)

    if (hours === 12 || hours === 13) {
      const isNZTimezone =
        timeZoneId === "Pacific/Auckland" ||
        timeZoneId === "NZ" ||
        timeZoneId.includes("Auckland") ||
        timeZoneId.includes("Pacific")
      const gotGMTFormat = shortTZ.startsWith("GMT+") || shortTZ.startsWith("GMT-")
      if (isNZTimezone || (gotGMTFormat && (hours === 12 || hours === 13))) {
        return hours === 13 ? "NZDT" : "NZST"
      }
    }

    if (shortTZ && !shortTZ.startsWith("GMT")) return shortTZ

    const longFormatter = new Intl.DateTimeFormat("en", { timeZoneName: "long" })
    const longParts = longFormatter.formatToParts(dateToFormat)
    const longTZ = longParts.find((part) => part.type === "timeZoneName")?.value ?? ""
    return shortTZ || longTZ || ""
  } catch {
    const offset = -dateToFormat.getTimezoneOffset()
    const hours = Math.floor(Math.abs(offset) / 60)
    const minutes = Math.abs(offset) % 60
    const sign = offset >= 0 ? "+" : "-"
    return `UTC${sign}${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
  }
}

function getUTCOffset(dateToFormat: Date): string {
  const offset = -dateToFormat.getTimezoneOffset()
  const totalMinutes = Math.abs(offset)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  const sign = offset >= 0 ? "+" : "-"
  const paddedMinutes = minutes.toString().padStart(2, "0")
  return minutes > 0 ? `UTC${sign}${hours}:${paddedMinutes}` : `UTC${sign}${hours}`
}

/**
 * Isolated clock + calibration dialog. Owns ticking date state so only this
 * component re-renders every second, not the rest of the header (logo, sidebar button).
 */
export function HeaderClockDialog() {
  const [date, setDateTo] = React.useState<Date>(() => new Date())
  const lastSecondsRef = React.useRef<number>(date.getSeconds())

  React.useEffect(() => {
    const interval = setInterval(() => {
      const update = new Date()
      const sec = update.getSeconds()
      if (sec !== lastSecondsRef.current) {
        lastSecondsRef.current = sec
        setDateTo(update)
      }
    }, 100)
    return () => clearInterval(interval)
  }, [])

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="ml-auto text-muted-foreground whitespace-nowrap text-sm md:text-base font-mono cursor-pointer hover:opacity-80 transition-opacity"
        >
          <Clock
            size="small"
            timezone="local"
            showSeconds={false}
            showTimezone={true}
            showDate={false}
            disableDialog={true}
            className="mr-1"
          />
          <span className="hidden md:inline">
            <span className="text-muted-foreground/70"> (</span>
            <Clock
              size="small"
              timezone="utc"
              showSeconds={false}
              showLabel={true}
              disableDialog={true}
              className="mx-0"
            />
            <span className="text-muted-foreground/70">)</span>
          </span>
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Camera Clock Calibration</DialogTitle>
          <DialogDescription>
            Photograph this screen with your camera to calibrate the clock on your camera.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-1">
            <p className="text-xs font-medium text-muted-foreground">Local Time</p>
            <p className="text-lg font-mono font-semibold">{formatFullDateTime(date, "local", "24h")}</p>
            <p className="text-xs text-muted-foreground">
              {getTimezoneAbbreviation(date)} ({getUTCOffset(date)})
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-xs font-medium text-muted-foreground">UTC Time</p>
            <p className="text-lg font-mono font-semibold">{formatFullDateTime(date, "utc", "24h")}</p>
            <p className="text-xs text-muted-foreground">UTC</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
